// Baselayer Results Explorer — Website Analysis tab. Reads the SHARED
// online-presence add-on (ordered as either WebsiteAnalysis or ReviewsSocials)
// and renders the website / online-presence fields. All fields UNVERIFIED →
// parsed defensively.

import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";
import { AddOnEmptyState } from "../AddOnEmptyState";
import { DefinitionList, formatLooseDate } from "../tabHelpers";
import { findAddOn, isCompleted } from "../tabHelpers";
import { asObject, asString, asNumber, pick, safeParse, display } from "../parse";

interface WebsiteTabProps {
  checkId: string;
  addOns: BackgroundCheckAddOnDto[] | undefined;
}

export function WebsiteTab({ checkId, addOns }: WebsiteTabProps) {
  // Website + Reviews&Socials share one online-presence add-on.
  const addOn = findAddOn(addOns, "WebsiteAnalysis", "ReviewsSocials");

  if (!addOn || !isCompleted(addOn)) {
    return (
      <AddOnEmptyState
        checkId={checkId}
        addOnType="WebsiteAnalysis"
        label="Website Analysis"
        title="No website analysis ordered"
        description="Order a website analysis to see online-presence signals for this subject."
        addOn={addOn}
      />
    );
  }

  const root = asObject(safeParse(addOn.rawResponseJson));
  const web =
    asObject(pick(root, "website", "online_presence", "web_presence")) ?? root;

  const domainAge = asNumber(pick(web, "domain_age", "domain_age_days"));

  const items = [
    {
      label: "Website",
      value: websiteLink(asString(pick(web, "url", "website", "domain"))),
    },
    { label: "Industry", value: display(pick(web, "industry", "category")) },
    {
      label: "Domain age",
      value: domainAge != null ? `${domainAge} days` : "—",
    },
    {
      label: "Domain created",
      value: formatLooseDate(asString(pick(web, "domain_created", "created_at"))),
    },
    {
      label: "Technologies",
      value: display(pick(web, "technologies", "tech_stack")),
    },
    {
      label: "Risk signals",
      value: display(pick(web, "risk_signals", "risk", "risk_score")),
    },
  ];

  return <DefinitionList items={items} />;
}

function websiteLink(url: string | null) {
  if (!url) return "—";
  const href = url.startsWith("http") ? url : `https://${url}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-gold-shadow hover:underline break-all"
    >
      {url}
    </a>
  );
}
